import React, { Component } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { TrevorIcon } from '@trevor/ui-components';
import { dateOptions } from '@/util/enums/EnumDateTimeAggregation';
import { DATE, DATETIME } from '@/util/ColumnType';
import { GroupByDropdownList } from '@/components/layers/LayerAggregate/subcomponents/GroupByDropdownList';
import {
  StepValue,
  StepValueAction,
} from '@/views/QueryBuilder/subcomponents/Steps';
import { safeOnClick } from '@/util/helpers/event-helpers';
import { getCurrentPks, getIsAnyStepFromSavedQuery } from '@/store-selectors';
import DropdownItem from '../util/DropdownItem';
import MultiDropdown from './MultiDropdown';
import cssSummarize from '../css/module/summarizeInline.css';
import flex from '../css/module/flex.css';
import DropdownButton from './DropdownButton';
import Checkbox from './configure/Checkbox';
import AggregateColumn from '../util/AggregateColumn';

const SummarizeGroupBysContainer = styled.div`
  display: flex;

  :has(${StepValue}):has([data-dropdown-button]),
  :has(${StepValue}):has([data-column-select]) {
    > ${StepValue} {
      margin-right: var(--spacing-xxs);
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;

      :hover {
        margin-right: 0;
      }
    }
    [data-dropdown-button] > ${StepValue} {
      margin-left: var(--spacing-xxs);
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;

      :hover {
        margin-left: 0;
      }
    }

    > ${StepValue} > div,
    [data-dropdown-button] > ${StepValue} > div {
      :hover {
        > div {
          margin-left: var(--spacing-xxs);
        }
      }
    }
  }
`;

class SummarizeGroupbys_LEGACY extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: {}, // an object containing a column object and true / false value
    };
    this.selectGroupBys = this.selectGroupBys.bind(this);
  }

  componentWillUnmount() {
    this.setState({
      open: {},
    });
  }

  // return true if a column is a primary key column. Used when we have the columnNameObj rather than the columnObject (which has it's own isPK property)
  isPk(columnNameObj) {
    const { currentPks, isFromSavedQuery, aliases } = this.props;
    if (isFromSavedQuery) {
      return currentPks
        .map((pk) => `${pk._alias}:${pk._name}`)
        .includes(`${columnNameObj._alias}:${columnNameObj._name}`);
    }

    return (
      columnNameObj.name() === 'id' &&
      columnNameObj.alias() &&
      aliases[columnNameObj.alias()]
    );
  }

  chosenGroupBys() {
    const { groupBys, columns, getTableOrInsightName } = this.props;
    const display = groupBys.map((colNameObj, i) => {
      const tableName = getTableOrInsightName(colNameObj.alias(), false, true);
      const column = this.isPk(colNameObj) ? tableName : colNameObj.name();
      const type = this.getColumnType(colNameObj);
      const dateSelect =
        type && (type === DATE || type === DATETIME) ? (
          <DropdownButton
            open={false}
            contentBuilder={() => (
              <MultiDropdown
                items={this.dateOptions((d) => this.updateDateGroupBy(d, i))}
                style={{ marginTop: '5px' }}
              />
            )}
          >
            <StepValue $type="summarise">
              {colNameObj.aggregation()}
              <StepValueAction>
                <TrevorIcon key="down" name="triangle-down" />
              </StepValueAction>
            </StepValue>
          </DropdownButton>
        ) : null;

      // if the group by is a table (i.e. it's a primary key column), add a dropdown containing a list of columns from that table to include

      // get all columns for this alias, and then filter again to remove options already added (as you can currently add a pk column after selecting columns from that same table);
      const relevantColumns = !this.isPk(colNameObj)
        ? null
        : columns
            .filter((_column) => _column.name().alias() === colNameObj.alias())
            .filter((_column) => {
              if (_column.isPk()) {
                return true;
              }
              return !groupBys.some(
                (gb) => gb.name() === _column.name().name(),
              );
            });

      // pass relevant columns to a dropdown with checkboxes
      const columnSelect = !this.isPk(colNameObj)
        ? null
        : this.groupByOptionsForIDColumns(relevantColumns, colNameObj);

      return (
        <SummarizeGroupBysContainer key={`groupBy_${i}`}>
          <StepValue $type="summarise">
            {column}
            <StepValueAction
              onClick={safeOnClick(() => this.delete('groupBys', colNameObj))}
            >
              <TrevorIcon key="close" name="close-small" />
            </StepValueAction>
          </StepValue>
          {dateSelect}
          {columnSelect}
        </SummarizeGroupBysContainer>
      );
    });
    return display;
  }

  // get column type from a columnName object
  getColumnType(columnNameObj) {
    const { columns } = this.props;
    const column = columns.find(
      (c) =>
        columnNameObj.name() === c.name().name() &&
        columnNameObj.alias() === c.name().alias(),
    ); // returns the matching Column object
    if (!column) {
      // happens if error because available columns doesn't include selected group by
      return null;
    }
    const type = column.type().type();
    return type;
  }

  // date dropdown options
  dateOptions(onClick) {
    return dateOptions.map((d) => {
      return DropdownItem.action(<span>{d}</span>, () => onClick(d));
    });
  }

  // populate checkbox dropdown (for use when id column is selected as a groupby option)
  // in old trevor, this would be the "include other columns" section of the summarize modal
  groupByOptionsForIDColumns(columns, pkcolumn) {
    const options = columns
      .filter((c) => c.isVisible() || c.isPk())
      .map((c) => {
        const idColumn = c.isPk();
        const value = idColumn ? true : this.extraColumnsInclude(c.name());
        const handleChange = () => {
          if (!idColumn) {
            this.addOrDeleteExtraColumn(c.name());
          }
        };
        const className = idColumn ? cssSummarize.cantUncheck : '';
        return DropdownItem.action(
          <div
            className={`${flex.alignItemsCenter} ${className}`}
            data-column-select
          >
            <Checkbox selected={value} className="margin-right-10" />
            {c.name().name()}
          </div>,
          handleChange,
          true, // don't close modal when user clicks on options
        );
      });
    const open = this.state.open[pkcolumn];
    return (
      <DropdownButton
        open={open}
        contentBuilder={() => (
          <MultiDropdown
            items={options}
            style={{
              maxHeight: '200px',
              overflow: 'scroll',
              marginTop: '5px',
            }}
          />
        )}
      >
        <StepValue $type="summarise">
          columns
          <StepValueAction>
            <TrevorIcon name="triangle-down" />
          </StepValueAction>
        </StepValue>
      </DropdownButton>
    );
  }

  // check to see if the selected groupbys include a column
  extraColumnsInclude(columnNameObj) {
    const { extraColumns } = this.props;
    const col = columnNameObj;
    return extraColumns.some(
      (gb) => gb.name() === col.name() && gb.alias() === col.alias(),
    );
  }

  // Add or delete groupby options in the checkbox dropdown
  addOrDeleteExtraColumn(columnNameObj) {
    // if col exists as a groupby option already, delete it
    if (this.extraColumnsInclude(columnNameObj)) {
      this.delete('extraColumns', columnNameObj);
    } else {
      // if it doesn't exist, add in
      this.selectExtraColumns(columnNameObj);
    }
  }

  // update groupby array (change from columnNameObj to AggregateColumn) when date or time is selected
  updateDateGroupBy(aggType, i) {
    const { groupBys } = this.props;
    const copyOfArray = groupBys.slice();
    // get columNameobj
    const columnName = copyOfArray[i].columnName();
    // create new aggregatecolumn
    const newObject = new AggregateColumn(
      columnName.alias(),
      columnName.name(),
      aggType,
    );
    copyOfArray[i] = newObject;
    this.props.setState({
      groupBys: copyOfArray,
    });
  }

  // delete a groupBy or extraColumn option by finding its position in the groupBys array, and then removing it.
  delete(targetState, columnNameObj) {
    const column = columnNameObj;
    const { extraColumns } = this.props;
    const array = this.props[targetState];
    // need to findIndex using a condition because we're looking through a list of AggregateColumns with a ColumnNameObj
    const index = array.findIndex(
      (a) => a.name() === column.name() && a.alias() === column.alias(),
    );
    const copyOfArray = array.slice();
    copyOfArray.splice(index, 1);

    if (targetState === 'groupBys') {
      // if the group by option is a table (i.e. a PK column) also remove any selected extra columns
      const extraColumnsNew = this.isPk(columnNameObj)
        ? this.removeAllExtraColumns(columnNameObj, extraColumns)
        : extraColumns;

      this.props.setState({
        groupBys: copyOfArray,
        extraColumns: extraColumnsNew,
      });
    } else {
      this.props.setState({
        extraColumns: copyOfArray,
      });
    }
  }

  removeAllExtraColumns(columnNameObj, extraColumns) {
    let newArray = extraColumns.slice();
    newArray = newArray.filter((ec) => ec.alias() !== columnNameObj.alias());
    return newArray;
  }

  // update groupby state when an option is clicked in menu with Aggregate Column
  selectGroupBys(columnNameObj, agg) {
    const { groupBys } = this.props;
    const object = new AggregateColumn(
      columnNameObj.alias(),
      columnNameObj.name(),
      agg,
    );

    // set open state to true if pk column
    const copyOfState = { ...this.state.open };
    if (this.isPk(columnNameObj)) {
      copyOfState[columnNameObj] = true;
      this.setState({
        open: copyOfState,
      });
    }

    this.props.setState({
      groupBys: [...groupBys, object],
    });
  }

  // update groupby state when an option is clicked in menu with Aggregate Column
  selectExtraColumns(columnNameObj) {
    this.props.setState({
      extraColumns: [...this.props.extraColumns, columnNameObj],
    });
  }

  render() {
    const { groupBys, columns, getTableOrInsightName, metricsCount, aliases } =
      this.props;
    return (
      <div className={`${flex.alignItemsCenter} ${flex.wrap}`}>
        <span className="margin-right-5">Dimensions</span>
        <div className={`${flex.alignItemsCenter} ${flex.wrap}`}>
          {this.chosenGroupBys()}
          <GroupByDropdownList
            columns={columns}
            getTableOrInsightName={getTableOrInsightName}
            groupBys={groupBys}
            aliases={aliases}
            selectGroupBys={this.selectGroupBys}
            dateOptions={this.dateOptions}
            metricsCount={metricsCount}
          />
        </div>
      </div>
    );
  }
}

const SummarizeGroupBys = (props) => {
  const currentPks = useSelector(getCurrentPks);
  const isFromSavedQuery = useSelector(getIsAnyStepFromSavedQuery);

  return (
    <SummarizeGroupbys_LEGACY
      {...props}
      currentPks={currentPks}
      isFromSavedQuery={isFromSavedQuery}
    />
  );
};

export default SummarizeGroupBys;
